import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api } from '../services/api';

type User = {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  role: 'admin' | 'manager' | 'employee';
  permissions: Record<string, boolean>;
};

type AuthContextType = {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Bootstrap probe for an existing session cookie. An explicit login() can
    // race ahead of this (the probe 401s because it fired before the cookie
    // existed), so it must never overwrite a user that login() already set.
    api
      .me()
      .then((data) => setUser((current) => current ?? data.user))
      .catch(() => {
        /* no session yet — user is already null unless login() beat us */
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email: string, password: string) => {
    const { user } = await api.login(email, password);
    setUser(user);
  };

  const logout = async () => {
    await api.logout();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
